import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";

interface Column<T> {
  key: keyof T | string;
  label: string;
  render?: (item: T, index: number) => React.ReactNode;
  isHtml?: boolean;
}

interface SupplierTableProps<T> {
  data: Record<string, T[]>; // grouped by part_no
  columns: Column<T>[];
  rowKey?: (item: T, index: number) => React.Key;
  className?: string;
  mobileLimitPerGroup?: number;
  emptyMessage?: string;
  theme?: "auto" | "light" | "dark";
}

function RcFactorCard({
  rc,
  factor,
  editable = false,
  onChange,
  className = "",
}: {
  rc?: string | number;
  factor?: string | number;
  editable?: boolean;
  onChange?: (payload: { rc?: string; factor?: string }) => void;
  className?: string;
}) {
  const handle = (key: "rc" | "factor", val: string) => {
    if (!onChange) return;
    onChange({
      ...(key === "rc" ? { rc: val } : {}),
      ...(key === "factor" ? { factor: val } : {}),
    });
  };

  return (
    <div className={`rc-factor-card ${className}`}>
      <div className="rc-col rc-col-rc">
        <div className="rc-label">RC</div>
        {editable ? (
          <input
            className="rc-input"
            value={String(rc ?? "")}
            onChange={(e) => handle("rc", e.target.value)}
            placeholder="RC"
            aria-label="rc"
          />
        ) : (
          <div className="rc-value" title={String(rc ?? "—")}>
            {rc ?? "—"}
          </div>
        )}
      </div>

      <div className="rc-col rc-col-factor">
        <div className="rc-label">Factor</div>
        {editable ? (
          <input
            className="rc-input"
            value={String(factor ?? "")}
            onChange={(e) => handle("factor", e.target.value)}
            placeholder="Factor"
            aria-label="factor"
          />
        ) : (
          <div className="rc-value" title={String(factor ?? "—")}>
            {factor ?? "—"}
          </div>
        )}
      </div>
    </div>
  );
}

function SupplierTable<T>({
  data,
  columns,
  rowKey = (_, index) => index,
  className = "",
  mobileLimitPerGroup = 5,
  emptyMessage = "No records found",
  theme = "auto",
}: SupplierTableProps<T>): JSX.Element {
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 767.98px)").matches
      : false
  );

  const getNestedValue = useCallback((obj: any, path: string): any => {
    return path
      .split(".")
      .reduce(
        (acc, key) => (acc && typeof acc === "object" ? acc[key] : undefined),
        obj
      );
  }, []);

  const renderCellValue = useCallback(
    (col: Column<T>, item: T, rowIndex: number) => {
      const rawValue = col.render
        ? col.render(item, rowIndex)
        : getNestedValue(item, col.key.toString());
      if (rawValue === undefined || rawValue === null) return "";
      if (React.isValidElement(rawValue)) return rawValue;
      return rawValue;
    },
    [getNestedValue]
  );

  const isImageUrl = useCallback((v: string) => {
    if (!v || typeof v !== "string") return false;
    const lower = v.toLowerCase();
    return (
      /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/.test(lower) ||
      lower.startsWith("http://") ||
      lower.startsWith("https://") ||
      lower.startsWith("/storage/")
    );
  }, []);

  const isSerialLabel = useCallback(
    (label: string) =>
      /(^|\s)(sr\.?\s*no|serial\s*no|s\.?r\.?\s*no)(\s|$)/i.test(label.trim()),
    []
  );

  const groupKeys = useMemo(() => Object.keys(data || {}), [data]);

  const actionColIndex = useMemo(
    () => columns.findIndex((c) => c.label.toLowerCase().includes("action")),
    [columns]
  );
  const actionCol = actionColIndex >= 0 ? columns[actionColIndex] : undefined;

  const excludeCols = useMemo(
    () =>
      new Set([
        "product.part_no",
        "principal.type",
        "action",
        // "date", "branch.name"
      ]),
    []
  );

  const visibleColumns = useMemo(
    () =>
      columns.filter(
        (c, idx) => !excludeCols.has(String(c.key)) && idx !== actionColIndex
      ),
    [columns, excludeCols, actionColIndex]
  );

  const formatDate = useCallback((val?: string) => {
    if (!val) return "—";
    const d = dayjs(val);
    return d.isValid() ? d.format("DD-MM-YYYY") : "—";
  }, []);

  // Desktop table row renderer
  const DesktopView = useCallback(
    () => (
      <div className={`table-responsive supplier-custom-table ${className}`}>
        <table
          className="table table-nowrap mb-0 supplier-table-desktop"
          role="table"
          aria-label="Supplier grouped table"
        >
          <thead>
            <tr>
              {visibleColumns.map((col, idx) => (
                <th key={`h-${idx}`} scope="col">
                  {col.label}
                </th>
              ))}
              {/* {actionCol ? <th scope="col">{actionCol.label}</th> : null} */}
            </tr>
          </thead>
          <tbody>
            {groupKeys.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length} className="text-center">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              groupKeys.map((groupKey) => {
                const groupItems = data[groupKey] || [];
                const first = (groupItems[0] as any) || {};
                const partNo = groupKey || "—";
                const principal = first?.principal?.type || "—";
                // const branch = first?.branch?.name || "—";
                // const dateRaw =
                //   first?.date || first?.created_at || first?.updated_at;
                // const formattedDate = formatDate(dateRaw);

                return (
                  <React.Fragment key={`g-${String(groupKey)}`}>
                    <tr className="supplier-group-header" aria-hidden="true">
                      <td colSpan={visibleColumns.length} className="px-3 py-2">
                        <div
                          className="group-header-row"
                          style={{ justifyContent: "center" }}
                        >
                          <div className="group-header-badges">
                            <span className="badge part-no">{partNo}</span>
                            <span className="badge principal">{principal}</span>
                            {/* <span className="badge date">{formattedDate}</span> */}
                            {/* <span className="badge branch">{branch}</span> */}
                            <span>
                              {actionCol && actionCol.render
                                ? actionCol.render(first, 0)
                                : actionCol
                                ? getNestedValue(first, String(actionCol.key))
                                : null}
                            </span>
                          </div>
                        </div>
                      </td>
                    </tr>

                    {groupItems.map((item, i) => (
                      <tr key={rowKey(item, i)}>
                        {visibleColumns.map((col, cIdx) => {
                          const value = renderCellValue(col, item, i);
                          const isHtml = col.isHtml === true;
                          const title =
                            !isHtml && typeof value === "string"
                              ? value
                              : undefined;

                          // image handling
                          if (typeof value === "string" && isImageUrl(value)) {
                            return (
                              <td
                                className={
                                  col.label.toLowerCase() === "description"
                                    ? "description-cell"
                                    : ""
                                }
                                key={`cell-${i}-${cIdx}`}
                                title={title}
                              >
                                <div className="small-thumb">
                                  <img
                                    src={value}
                                    alt={String(
                                      getNestedValue(item, "part_no") || ""
                                    )}
                                    onError={(e) =>
                                      ((
                                        e.currentTarget as HTMLImageElement
                                      ).style.display = "none")
                                    }
                                  />
                                </div>
                              </td>
                            );
                          }

                          return (
                            <td key={`cell-${i}-${cIdx}`} title={title}>
                              {isHtml && typeof value === "string" ? (
                                <span
                                  dangerouslySetInnerHTML={{ __html: value }}
                                />
                              ) : String(col.key) === "password" ? (
                                "********"
                              ) : (
                                (value as React.ReactNode)
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    ),
    [
      data,
      visibleColumns,
      actionCol,
      groupKeys,
      className,
      emptyMessage,
      formatDate,
      renderCellValue,
      isImageUrl,
      getNestedValue,
      rowKey,
    ]
  );

  const MobileView = useCallback(
    () => (
      <div className={`supplier-mobile ${className}`}>
        {groupKeys.length === 0 ? (
          <div className="rt-card-list">
            <div className="rt-card">
              <div className="rt-card-row">
                <div className="rt-card-value" style={{ textAlign: "center" }}>
                  {emptyMessage}
                </div>
              </div>
            </div>
          </div>
        ) : (
          groupKeys.map((groupKey) => {
            const groupItems = data[groupKey] || [];
            const first = (groupItems[0] as any) || {};
            const partNo = groupKey || "—";
            const principal = first?.principal?.type || "—";
            const branch = first?.branch?.name || "—";
            const dateRaw =
              first?.date || first?.created_at || first?.updated_at;
            const formattedDate = formatDate(dateRaw);

            const itemsToShow = groupItems.slice(0, mobileLimitPerGroup);

            return (
              <div
                key={`group-mobile-${String(groupKey)}`}
                style={{ marginBottom: 12 }}
              >
                <div style={{ marginBottom: 8 }}>
                  <div className="group-header-badges" aria-hidden="true">
                    <span className="badge part-no">{partNo}</span>
                    <span className="badge principal">{principal}</span>
                    {/* <span className="badge date">{formattedDate}</span> */}
                    {/* <span className="badge branch">{branch}</span> */}
                    <span>
                      <span>
                        {actionCol && actionCol.render
                          ? actionCol.render(first, 0)
                          : actionCol
                          ? getNestedValue(first, String(actionCol.key))
                          : null}
                      </span>
                    </span>
                  </div>
                </div>

                <div
                  className="rt-card-list"
                  role="list"
                  aria-label={`Items for ${partNo}`}
                >
                  {itemsToShow.map((item, idx) => {
                    const imgCol = columns.find((c) => {
                      const v = renderCellValue(c, item, idx);
                      return typeof v === "string" && isImageUrl(v as string);
                    });

                    const headerKeys: string[] = [];
                    if (imgCol) headerKeys.push(imgCol.key.toString());

                    const nonActionCols = columns.filter(
                      (c) =>
                        !c.label.toLowerCase().includes("action") &&
                        (!imgCol ||
                          c.key.toString() !== imgCol.key.toString()) &&
                        !isSerialLabel(String(c.label))
                    );

                    for (
                      let k = 0;
                      k < nonActionCols.length && headerKeys.length < 2;
                      k++
                    )
                      headerKeys.push(nonActionCols[k].key.toString());

                    const actionCols = columns.filter((c) =>
                      c.label.toLowerCase().includes("action")
                    );
                    const actionNodes = actionCols
                      .map((ac) => renderCellValue(ac, item, idx))
                      .filter((n) => n !== undefined && n !== null);

                    const rcCol = columns.find(
                      (c) =>
                        String(c.key).toLowerCase() === "rc" ||
                        c.label.toLowerCase() === "rc"
                    );
                    const factorCol = columns.find(
                      (c) =>
                        String(c.key).toLowerCase() === "factor" ||
                        c.label.toLowerCase() === "factor"
                    );
                    const rcVal = rcCol
                      ? renderCellValue(rcCol, item, idx)
                      : undefined;
                    const factorVal = factorCol
                      ? renderCellValue(factorCol, item, idx)
                      : undefined;

                    return (
                      <div
                        className="rt-card"
                        key={rowKey(item, idx)}
                        role="listitem"
                      >
                        <div className="rt-card-head">
                          <div className="card-left">
                            {imgCol
                              ? (() => {
                                  const img = renderCellValue(
                                    imgCol,
                                    item,
                                    idx
                                  ) as string;
                                  return (
                                    <div
                                      className="rt-thumb"
                                      title={String(img)}
                                    >
                                      <img
                                        src={img}
                                        alt={imgCol.label}
                                        onError={(e) =>
                                          ((
                                            e.currentTarget as HTMLImageElement
                                          ).style.display = "none")
                                        }
                                      />
                                    </div>
                                  );
                                })()
                              : (() => {
                                  const firstHeaderKey = headerKeys[0];
                                  if (!firstHeaderKey) return null;
                                  const firstHeaderCol = columns.find(
                                    (c) => c.key.toString() === firstHeaderKey
                                  );
                                  if (!firstHeaderCol) return null;
                                  const v = renderCellValue(
                                    firstHeaderCol,
                                    item,
                                    idx
                                  );
                                  return (
                                    <div style={{ minWidth: 0 }}>
                                      <div className="card-title">
                                        {firstHeaderCol.label}
                                      </div>
                                      <div className="card-sub">
                                        {v as React.ReactNode}
                                      </div>
                                    </div>
                                  );
                                })()}

                            {!imgCol && headerKeys[1]
                              ? (() => {
                                  const secondKey = headerKeys[1];
                                  const secondCol = columns.find(
                                    (c) => c.key.toString() === secondKey
                                  );
                                  if (!secondCol) return null;
                                  const v = renderCellValue(
                                    secondCol,
                                    item,
                                    idx
                                  );
                                  return (
                                    <div style={{ minWidth: 0 }}>
                                      <div className="second-label">
                                        {secondCol.label}
                                      </div>
                                      <div className="card-sub">
                                        {v as React.ReactNode}
                                      </div>
                                    </div>
                                  );
                                })()
                              : null}
                          </div>
                        </div>

                        <div style={{ marginBottom: 8 }}>
                          <RcFactorCard
                            rc={String(rcVal ?? "")}
                            factor={String(factorVal ?? "")}
                            editable={false}
                          />
                        </div>

                        <div className="rt-card-body">
                          {columns.map((col, cIdx) => {
                            const keyStr = col.key.toString();
                            if (col.label.toLowerCase().includes("action"))
                              return null;
                            if (headerKeys.includes(keyStr)) {
                              if (headerKeys.indexOf(keyStr) < 2) return null;
                            }
                            if (isSerialLabel(String(col.label))) return null;

                            const value = renderCellValue(col, item, idx);
                            if (
                              typeof value === "string" &&
                              isImageUrl(value as string)
                            )
                              return null;
                            if (
                              value === "" ||
                              value === undefined ||
                              value === null
                            )
                              return null;

                            const content =
                              col.isHtml && typeof value === "string" ? (
                                <span
                                  dangerouslySetInnerHTML={{ __html: value }}
                                />
                              ) : String(col.key) === "password" ? (
                                "********"
                              ) : (
                                (value as React.ReactNode)
                              );

                            return (
                              <div
                                className="rt-card-row"
                                key={`m-${idx}-${cIdx}`}
                              >
                                <div className="rt-card-label">{col.label}</div>
                                <div
                                  className="rt-card-value"
                                  title={
                                    typeof value === "string"
                                      ? value
                                      : undefined
                                  }
                                >
                                  {content}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {groupItems.length > mobileLimitPerGroup && (
                  <div className="rt-footer">
                    Showing {mobileLimitPerGroup} of {groupItems.length} items
                    for {partNo}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    ),
    [
      data,
      columns,
      className,
      emptyMessage,
      groupKeys,
      mobileLimitPerGroup,
      formatDate,
      isImageUrl,
      isSerialLabel,
      renderCellValue,
      rowKey,
    ]
  );

  // merged CSS injection (idempotent)
  useEffect(() => {
    const id = "supplier-table-merged-styles";
    if (document.getElementById(id)) return;

    const css = `
      /* Theme variables - light defaults (rf = rate-fc styles) and rt = table styles */
      :root {
        --rf-card-bg: #ffffff;
        --rf-border: rgba(0,0,0,0.06);
        --rf-text: #0f1724;
        --rf-muted: #6b7280;
        --rf-input-bg: #ffffff;
        --rf-input-border: rgba(16,24,40,0.06);
        --rf-input-color: #0f1724;
        --rf-shadow: 0 1px 2px rgba(0,0,0,0.04);
        --rf-action-1-bg: rgba(14,165,233,0.08);
        --rf-action-1-border: rgba(14,165,233,0.12);
        --rf-action-1-color: #0369a1;
        --rf-action-2-bg: rgba(239,68,68,0.06);
        --rf-action-2-border: rgba(239,68,68,0.12);
        --rf-action-2-color: #b91c1c;
        --rf-radius: 10px;
        --rf-gap: 12px;

        --rt-card-bg: #ffffff;
        --rt-border-color: rgba(0,0,0,0.06);
        --rt-text-color: #0f172a;
        --rt-label-color: #4b5563;
        --rt-value-color: #111827;
        --rt-shadow: 0 1px 0 rgba(16,24,40,0.04);
        --rt-accent: #2563eb;
        --rt-thumb-bg: #fff;
        --rt-thumb-border: rgba(0,0,0,0.06);
        --rt-footer-color: #6b7280;
      }

      @media (prefers-color-scheme: dark) {
        :root {
          --rf-card-bg: #071122;
          --rf-border: rgba(255,255,255,0.04);
          --rf-text: #e6eef8;
          --rf-muted: #9ca3af;
          --rf-input-bg: #0f1724;
          --rf-input-border: rgba(255,255,255,0.06);
          --rf-input-color: #a3a6a6;
          --rf-shadow: 0 6px 18px rgba(2,6,23,0.4);
          --rf-action-1-bg: rgba(3,169,244,0.06);
          --rf-action-1-border: rgba(3,169,244,0.12);
          --rf-action-1-color: #38bdf8;
          --rf-action-2-bg: rgba(244,63,94,0.06);
          --rf-action-2-border: rgba(244,63,94,0.12);
          --rf-action-2-color: #fb7185;

          --rt-card-bg: #071022;
          --rt-border-color: rgba(255,255,255,0.04);
          --rt-text-color: #e6eef8;
          --rt-label-color: #e6eef8;
          --rt-value-color: #cbd5e1;
          --rt-accent: #a78bfa;
          --rt-thumb-bg: #071022;
          --rt-thumb-border: rgba(255,255,255,0.04);
          --rt-footer-color: #9ca3af;
        }
      }

      body[data-theme="dark"], body.dark-theme, [data-theme="dark"] {
        --rf-card-bg: #071122;
        --rf-border: rgba(255,255,255,0.04);
        --rf-text: #e6eef8;
        --rf-muted: #9ca3af;
        --rf-input-bg: #0f1724;
        --rf-input-border: rgba(255,255,255,0.06);
        --rf-input-color: #a3a6a6;
        --rf-shadow: 0 6px 18px rgba(2,6,23,0.4);
        --rf-action-1-bg: rgba(3,169,244,0.06);
        --rf-action-1-border: rgba(3,169,244,0.12);
        --rf-action-1-color: #38bdf8;
        --rf-action-2-bg: rgba(244,63,94,0.06);
        --rf-action-2-border: rgba(244,63,94,0.12);
        --rf-action-2-color: #fb7185;

        --rt-card-bg: #071022;
        --rt-border-color: rgba(255,255,255,0.04);
        --rt-text-color: #e6eef8;
        --rt-label-color: #e6eef8;
        --rt-value-color: #cbd5e1;
        --rt-accent: #a78bfa;
        --rt-thumb-bg: #071022;
        --rt-thumb-border: rgba(255,255,255,0.04);
        --rt-footer-color: #9ca3af;
      }

      /* supplier / table styles (merged) */
      .supplier-table-root { font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial; }
      .table-responsive { overflow-x: auto; }
      .supplier-table-desktop th, .supplier-table-desktop td { vertical-align: middle; }
      .small-thumb { width:60px; height:60px; border-radius:6px; overflow:hidden; display:inline-block; border:1px solid var(--rt-border-color); background:var(--rt-card-bg); }
      .small-thumb img { width:100%; height:100%; object-fit:contain; display:block; }

      /* card styles */
      .rt-card { padding:12px 14px; border-radius:var(--rf-radius); background:var(--rt-card-bg); border:1px solid var(--rt-border-color); box-shadow:var(--rt-shadow); color:var(--rt-text); transition: background-color 200ms ease, color 200ms ease, border-color 200ms ease, box-shadow 200ms ease; overflow: visible; }
      .rt-card-list { display:flex; flex-direction:column; gap:var(--rf-gap); }
      .rt-card-row { display:flex; justify-content:space-between; gap:12px; align-items:center; margin-bottom:8px; }
      .rt-card-label { flex:0 0 40%; font-weight:700; color:var(--rt-label-color); font-size:13px; }
      .rt-card-value { flex:1 1 60%; color:var(--rt-value-color); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-size:13px; }
      .rt-thumb { width:48px; height:48px; border-radius:8px; overflow:hidden; display:inline-block; background:var(--rt-thumb-bg); border:1px solid var(--rt-thumb-border); }
      .rt-thumb img{ width:100%; height:100%; object-fit:cover; }
      .rt-action > * { min-width:34px; height:34px; padding:6px; border-radius:8px; }

      /* rc factor */
      .rc-factor-card { display:flex; gap:12px; align-items:center; width:100%; }
      .rc-col { min-width:0; }
      .rc-col-rc { flex:0 0 42%; }
      .rc-col-factor { flex:1 1 58%; }
      .rc-label { font-size:13px; font-weight:600; color:var(--rt-label-color); }
      .rc-value { margin-top:6px; font-size:14px; color:var(--rt-value-color); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
      .rc-input { margin-top:6px; width:100%; padding:6px 8px; border-radius:6px; border:1px solid var(--rt-border-color); background:var(--rf-input-bg); color:var(--rf-input-color); }

      .group-header-row { display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap; }
      .group-header-badges { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
      .group-header-badges .badge { padding:6px 8px; border-radius:8px; font-weight:600; }
      .group-header-badges .part-no { background:var(--rt-accent); color:#fff; }
      .group-header-badges .principal { background:#0ea5e9; color:#fff; }
      .group-header-badges .date { background:#6b7280; color:#fff; }
      .group-header-badges .branch { background:var(--rt-accent); color:#fff; }

      .rt-footer { margin-top:8px; font-size:0.85rem; color:var(--rt-footer-color); text-align:right; }

      /* rf card styles (rate-fc) */
      .rf-card-list { display:flex; flex-direction:column; gap:var(--rf-gap); padding:6px 0; }
      .rf-card { background: var(--rf-card-bg); border: 1px solid var(--rf-border); border-radius: var(--rf-radius); padding: 12px; box-shadow: var(--rf-shadow); color: var(--rf-text); transition: transform .12s ease, box-shadow .12s ease, background .18s ease; }
      .rf-card:hover { transform: translateY(-2px); }
      .rf-card-row { display:flex; gap:8px; align-items:center; justify-content:space-between; margin-bottom:10px; }
      .rf-label { flex: 0 0 40%; font-weight:600; color: var(--rf-muted); font-size:13px; min-width:0; }
      .rf-value { flex: 1 1 60%; min-width:0; }
      .rf-compact-controls { display:flex; gap:8px; align-items:center; justify-content:flex-end; }
      .rf-thumb-placeholder { width:48px; height:48px; border-radius:6px; background: linear-gradient(#f6f7f8,#f3f4f6); border:1px solid var(--rf-border); flex:0 0 48px; }
      .rf-row-top { display:flex; gap:12px; align-items:center; justify-content:space-between; margin-bottom:8px; }
      .rf-card .form-control, .rf-card .tagging .react-select__control, .rf-card .react-select__control { width:100%; box-sizing:border-box; background: var(--rf-input-bg); color: var(--rf-input-color); border: 1px solid var(--rf-input-border); border-radius: 8px; box-shadow: none; }
      .rf-card .react-select__single-value, .rf-card .react-select__placeholder { color: var(--rf-input-color); }
      .rf-action-buttons { display:inline-flex; gap:8px; }
      .rf-action-btn { min-width:36px; height:36px; padding:0 8px; border-radius:999px; display:inline-flex; align-items:center; justify-content:center; font-size:14px; cursor:pointer; border:1px solid transparent; background: transparent; }
      .rf-action-btn.add { background: var(--rf-action-1-bg); border-color: var(--rf-action-1-border); color: var(--rf-action-1-color); }
      .rf-action-btn.del { background: var(--rf-action-2-bg); border-color: var(--rf-action-2-border); color: var(--rf-action-2-color); }

      /* small-screen tweaks */
      @media (max-width:420px) {
        .rt-card { padding:10px 12px; border-radius:8px; }
        .rt-card-row { margin-bottom:8px; }
        .rt-card-label, .rt-card-value { font-size:13px; }
        .rf-label { font-size:13px; }
        .rf-card { padding:10px; border-radius:8px; }
      }

      /* dark theme form control fallback */
      [data-theme="dark"] .form-control, [data-theme="dark"] .form-select,
      body.dark-theme .form-control, body.dark-theme .form-select {
        border-color: #393947;
        background: #171724;
        color: #a3a6a6;
        box-shadow: none;
      }

      .supplier-table-desktop th, .supplier-table-desktop td { vertical-align: middle; }

/* description column wrap fix */
.supplier-table-desktop{
  table-layout: auto;
  width: 100%;
}



.supplier-table-desktop td:not(.description-cell) {
    white-space: normal !important;
    word-wrap: break-word;
    word-break: break-word;
    max-width: auto;
    overflow: hidden;
}
    `;

    const node = document.createElement("style");
    node.id = id;
    node.appendChild(document.createTextNode(css));
    document.head.appendChild(node);
  }, []);

  return (
    <div
      className="supplier-table-root"
      data-theme={theme === "auto" ? "auto" : theme}
    >
      {isMobile ? <MobileView /> : <DesktopView />}
    </div>
  );
}

export default SupplierTable;
